// GCCV2CompanionResult.tsx
import React, { useState } from "react";
import { Send, RotateCcw, MessageCircle } from "lucide-react";
import AstriaGCCV2Shell, {
  SHELL_COLORS_GCC_V2,
  getGCCV2Fonts,
} from "./AstriaGCCV2Shell";
import {
  GCCV2ResponseContainer,
  GCCV2Card,
  GCCV2PlainTextCard,
  gccV2LoadingSpinner,
  gccV2SectionKindLabel,
} from "./AstriaGCCV2Card";
import { useGCCV2TabResult } from "./useGCCV2TabResult";
import {
  GCCV2Language,
  getGCCV2StrictLanguageInstruction,
} from "../../../utils/GCC/astriaGCCV2Language";
import { LC, L, useLnLanguage } from "../../../utils/lnText";

// ─── GCC Companion v2 — the one dashboard tab with a live conversational
// input, per spec's "Daily companion / Reflection / Conversation / Gentle
// check-in / Input area" purpose. Follow-up reflections are appended as
// new cards below the initial daily check-in, rather than replacing it.

const PROMPT = {
  en: (dob: string) =>
    `Give me my GCC Companion check-in for today — a gentle reflection and conversation opener.\nDate of Birth: ${dob}\n${getGCCV2StrictLanguageInstruction("en")}`,
  ar: (dob: string) =>
    `أعطني تسجيل الحضور الخاص بـ"الرفيق" اليوم — تأمل هادئ وبداية محادثة.\nتاريخ الميلاد: ${dob}\n${getGCCV2StrictLanguageInstruction("ar")}`,
};

const COPY = {
  title: { en: "Your Companion", ar: "رفيقك" },
  subtitle: {
    en: "A daily space for reflection and gentle conversation.",
    ar: "مساحة يومية للتأمل والمحادثة الهادئة.",
  },
  refresh: { en: "New Check-in", ar: "تسجيل حضور جديد" },
  placeholder: {
    en: "Share what's on your mind…",
    ar: "شاركني ما يدور في ذهنك…",
  },
  send: { en: "Send", ar: "إرسال" },
  chat: { en: "Chat", ar: "الدردشة" },
};

const GCCV2CompanionResult: React.FC = () => {
  useLnLanguage();
  const { loading, error, parsed, language, refresh, sendFollowUp, goToChat } =
    useGCCV2TabResult({
      tabKey: "companion",
      subCategoryMatch: "companion",
      buildPrompt: (dob, lang: GCCV2Language) => PROMPT[lang](dob),
    });
  const fonts = getGCCV2Fonts(language);

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [followUps, setFollowUps] = useState<
    { role: "user" | "ai"; text: string }[]
  >([]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setFollowUps((prev) => [...prev, { role: "user", text: trimmed }]);
    setInput("");
    try {
      await sendFollowUp(trimmed);
    } finally {
      setSending(false);
    }
  };

  return (
    <AstriaGCCV2Shell active="companion">
      <div style={{ marginBottom: 20 }}>
        <div
          className="flex items-center justify-between gap-3"
          style={{ marginBottom: 6 }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: SHELL_COLORS_GCC_V2.titleColor,
              fontFamily: fonts.heading,
            }}
          >
            {LC(COPY.title, language)}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-full text-xs font-medium border transition-all hover:bg-black/[0.02] disabled:opacity-50"
            style={{
              borderColor: SHELL_COLORS_GCC_V2.cardBorder,
              color: SHELL_COLORS_GCC_V2.mutedColor,
              background: "transparent",
              paddingInlineStart: 12,
              paddingInlineEnd: 12,
              paddingTop: 7,
              paddingBottom: 7,
            }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {LC(COPY.refresh, language)}
          </button>
        </div>
        <p
          style={{
            fontSize: 13.5,
            color: SHELL_COLORS_GCC_V2.mutedColor,
            lineHeight: 1.6,
          }}
        >
          {LC(COPY.subtitle, language)}
        </p>
      </div>

      {loading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            gap: 16,
          }}
        >
          {gccV2LoadingSpinner()}
          <p style={{ fontSize: 13, color: SHELL_COLORS_GCC_V2.mutedColor }}>
            {language === "ar"
              ? "نُعِدّ مساحتك بهدوء…"
              : L("Quietly preparing your space…")}
          </p>
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            padding: "16px 18px",
            background: "#FBEDE9",
            border: "1px solid #E8C6BB",
            borderRadius: 12,
            color: "#95493A",
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && parsed && (
        <GCCV2ResponseContainer>
          {parsed.sections.map((section, idx) =>
            section.kind === "generic" && parsed.sections.length === 1 ? (
              <GCCV2PlainTextCard
                key={idx}
                header={section.heading || LC(COPY.title, language)}
                text={section.body}
                delay={idx * 0.08}
                fontFamily={fonts.body}
              />
            ) : (
              <GCCV2Card
                key={idx}
                header={section.heading || gccV2SectionKindLabel(section.kind, language)}
                mainInsight={section.body}
                microText={
                  section.kind === "generic"
                    ? undefined
                    : gccV2SectionKindLabel(section.kind, language)
                }
                delay={idx * 0.08}
                fontFamily={fonts.body}
                alt={idx % 2 === 1}
              />
            ),
          )}

          {followUps.map((entry, idx) =>
            entry.role === "user" ? (
              <div
                key={`followup-${idx}`}
                style={{
                  alignSelf: "flex-end",
                  background: SHELL_COLORS_GCC_V2.accentSoft,
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontSize: 14,
                  color: SHELL_COLORS_GCC_V2.titleColor,
                  maxWidth: "80%",
                  marginInlineStart: "auto",
                }}
              >
                {entry.text}
              </div>
            ) : (
              <GCCV2PlainTextCard
                key={`followup-${idx}`}
                header={gccV2SectionKindLabel("closing", language)}
                text={entry.text}
                fontFamily={fonts.body}
              />
            ),
          )}
        </GCCV2ResponseContainer>
      )}

      {!loading && !error && parsed && (
        <button
          onClick={goToChat}
          className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
          style={{
            marginTop: 24,
            width: "100%",
            background: `linear-gradient(135deg, ${SHELL_COLORS_GCC_V2.accent} 0%, #E3C77A 100%)`,
            boxShadow: `0 4px 16px ${SHELL_COLORS_GCC_V2.accent}44`,
            paddingTop: 14,
            paddingBottom: 14,
          }}
        >
          <MessageCircle className="w-4 h-4" />
          {LC(COPY.chat, language)}
        </button>
      )}

      {/* ─── Input Area ─────────────────────────────────────────────────── */}
      {/* {!loading && (
        <div
          style={{
            marginTop: 20,
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            placeholder={COPY.placeholder[language]}
            disabled={sending}
            className="flex-1 rounded-full border text-sm focus:outline-none transition-all duration-200"
            style={{
              borderColor: SHELL_COLORS_GCC_V2.cardBorder,
              color: SHELL_COLORS_GCC_V2.titleColor,
              background: SHELL_COLORS_GCC_V2.white,
              fontFamily: fonts.body,
              paddingInlineStart: 18,
              paddingInlineEnd: 18,
              paddingTop: 12,
              paddingBottom: 12,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = SHELL_COLORS_GCC_V2.accent;
              e.currentTarget.style.boxShadow = SHELL_COLORS_GCC_V2.accentGlow;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor =
                SHELL_COLORS_GCC_V2.cardBorder;
              e.currentTarget.style.boxShadow = "none";
            }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !input.trim()}
            className="flex items-center justify-center rounded-full transition-all disabled:opacity-50"
            style={{
              width: 44,
              height: 44,
              background: `linear-gradient(135deg, ${SHELL_COLORS_GCC_V2.accent} 0%, #E3C77A 100%)`,
              color: "#FFFFFF",
              flexShrink: 0,
            }}
          >
            <Send
              className="w-4 h-4"
              style={{
                transform: language === "ar" ? "scaleX(-1)" : undefined,
              }}
            />
          </button>
        </div>
      )} */}
    </AstriaGCCV2Shell>
  );
};

export default GCCV2CompanionResult;
